import { Link, useLocation } from 'react-router-dom'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

interface MobileNavProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  displayName: string
  isAdmin: boolean
  isLoggingOut: boolean
  onLogout: () => void
}

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/join', label: 'Play' },
  { href: '/worlds', label: 'Worlds' },
]

export function MobileNav({
  open,
  onOpenChange,
  displayName,
  isAdmin,
  isLoggingOut,
  onLogout,
}: MobileNavProps) {
  const location = useLocation()

  const isActiveLink = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  const handleLinkClick = () => {
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-72">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center justify-between">
            <span>Navigation</span>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={handleLinkClick}
              className={cn(
                'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActiveLink(link.href)
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <Separator className="my-6" />

        <div className="space-y-4">
          <div className="flex items-center gap-2 px-3">
            <span className="text-sm font-medium">{displayName}</span>
            {isAdmin && (
              <Badge variant="secondary" className="text-xs">
                Admin
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            className="w-full text-red-600 hover:text-red-600"
            onClick={onLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? 'Logging out...' : 'Log out'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
